import React from "react";
import { Link } from "react-router-dom";
import { Spinner } from "@/components/common/Primitives";
import { getContactEmails } from "@/lib/settings";
import { useSettings } from "@/hooks/useSiteQueries";

function Section({ title, children }) {
  return (
    <section className="space-y-3">
      <h2 className="font-serif text-xl sm:text-2xl font-semibold text-maroon">{title}</h2>
      <div className="text-ink-muted leading-relaxed space-y-3">{children}</div>
    </section>
  );
}

export default function PrivacyPolicyPage() {
  const { data: settings, isPending } = useSettings();
  const contactEmails = getContactEmails(settings || {});
  const contactEmail = contactEmails[0] || "office@bcsabha.org";

  if (isPending) return <Spinner className="py-32" />;

  return (
    <div data-testid="privacy-page">
      <section className="bg-maroon text-white">
        <div className="container-bcs py-20 sm:py-24">
          <p className="eyebrow !text-gold-light mb-4">Legal</p>
          <h1 className="heading-xl !text-white max-w-3xl">Privacy Policy</h1>
          <p className="mt-6 text-white/80 max-w-2xl text-lg leading-relaxed">
            How The Bombay Catholic Sabha collects, uses and protects information when you use this website.
          </p>
          <p className="mt-4 text-sm text-white/60">Last updated: 10 July 2026</p>
        </div>
      </section>

      <section className="container-bcs py-14 sm:py-16 max-w-3xl">
        <div className="space-y-10">
          <Section title="Who we are">
            <p>
              This website is operated by <strong>The Bombay Catholic Sabha</strong> (“BCS”, “we”, “us”), a
              recognised laity organisation of the Archdiocese of Bombay. If you have questions about this
              policy, contact us at{" "}
              <a href={`mailto:${contactEmail}`} className="text-maroon hover:underline">
                {contactEmail}
              </a>
              .
            </p>
          </Section>

          <Section title="Information we collect">
            <p>Depending on how you use the site, we may collect:</p>
            <ul className="list-disc pl-5 space-y-2">
              <li>
                <strong>Contact and form submissions</strong> — name, email, phone, parish/unit and message
                content when you submit a form on the Contact page or other forms published on the site.
              </li>
              <li>
                <strong>Membership search</strong> — search terms you enter to look up membership records.
                We do not ask you to create a public account to browse the site.
              </li>
              <li>
                <strong>Technical data</strong> — standard server and browser logs (e.g. IP address, browser
                type, pages visited) that hosting providers may record for security and performance.
              </li>
            </ul>
          </Section>

          <Section title="How we use your information">
            <p>We use information to:</p>
            <ul className="list-disc pl-5 space-y-2">
              <li>Respond to enquiries and form submissions</li>
              <li>Provide membership and unit search features</li>
              <li>Operate and improve the website</li>
              <li>Comply with applicable law and organisational requirements</li>
            </ul>
            <p>We do not sell your personal information.</p>
          </Section>

          <Section title="Where data is stored">
            <p>
              Website content and form submissions are stored using secure cloud services (including Supabase
              for database operations). Public images and documents may be served from Cloudflare R2. These
              providers process data on our behalf under their own security and privacy terms.
            </p>
          </Section>

          <Section title="Cookies and local storage">
            <p>
              This public site does not use advertising or analytics cookies. Your browser may store small
              amounts of data (such as preferences or session information) needed for the site to function.
              If we add analytics or similar tools in future, we will update this policy and, where required,
              ask for your consent before non-essential cookies are set.
            </p>
            <p>
              The separate <Link to="/admin/login" className="text-maroon hover:underline">admin area</Link>{" "}
              uses authentication storage for authorised staff only.
            </p>
          </Section>

          <Section title="Third-party links and content">
            <p>
              The site may link to social media, YouTube, sponsor websites and other external services. We
              are not responsible for the privacy practices of those third parties. Fonts may be loaded from
              Google Fonts; see Google’s privacy policy for how they handle such requests.
            </p>
          </Section>

          <Section title="Data retention">
            <p>
              We keep form submissions and related records for as long as needed to handle your request,
              maintain organisational records, or meet legal obligations. Contact us if you wish to enquire
              about retention of information you have submitted.
            </p>
          </Section>

          <Section title="Your rights">
            <p>
              You may request access to, correction of, or deletion of personal information we hold about
              you, subject to applicable law (including India’s Digital Personal Data Protection Act where
              it applies). To make a request, email{" "}
              <a href={`mailto:${contactEmail}`} className="text-maroon hover:underline">
                {contactEmail}
              </a>
              .
            </p>
          </Section>

          <Section title="Changes to this policy">
            <p>
              We may update this page from time to time. The “Last updated” date at the top will change when
              we do. Continued use of the site after changes constitutes acceptance of the updated policy.
            </p>
          </Section>

          <div className="pt-4 border-t border-gray-200">
            <Link to="/contact" className="text-maroon font-medium hover:underline">
              Contact us →
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
